import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { isEqual } from 'lodash';

import { Tooltip } from '@mui/material';

import { SimpleTable } from '../molecules/simple-table';
import { useSelector } from '../store/utils';
import type { Store } from '../../../core/src/store/types';
import { Row } from '../atoms/row';
import { Circle } from '@mui/icons-material';

type Props = {
  title?: ReactNode;
};

export const CloudPlugsList = ({ title }: Props) => {
  const [devicesStatus] = useSelector((s) => {
    return [
      Object.values(s.variables.MQTT_CLOUD_DEVICES_STATUS).filter(
        ({ type }) =>
          type === 'shelly-plus-plug-s' ||
          type === 'shelly-plus-plug-s-gen3' ||
          type === 'shelly-plug-m-gen3' ||
          type === 'shelly-1pm-gen3' ||
          type === 'shelly-1pm-gen4' ||
          type === 'shelly-pro-1pm' ||
          type === 'shelly-pro-1pm-gen3'
      ),
    ] as const;
  }, isEqual);

  return (
    <SimpleTable
      title={title}
      rows={[
        [
          <FormattedMessage
            id="cloud_plugs.list.id"
            defaultMessage="ID"
            key={0}
          />,
          <FormattedMessage
            id="cloud_plugs.list.online"
            defaultMessage="Online"
            key={1}
          />,
          <FormattedMessage
            id="cloud_plugs.list.led_mode"
            defaultMessage="LED signalizace"
            key={2}
          />,
          <FormattedMessage
            id="cloud_plugs.list.relay"
            defaultMessage="Relé"
            key={3}
          />,
        ],
        ...devicesStatus.map(
          ({ plugId, status, lastMessageAt, ledMode, ledColors, ...plug }) => {
            return [
              plugId,
              <Tooltip title={String(lastMessageAt)} key={1}>
                <span>{status}</span>
              </Tooltip>,
              <CloudPlugsLedModeStatus
                ledMode={ledMode}
                ledColors={ledColors}
                key={2}
              />,
              <CloudPlugsSwitchStatus switch={plug.switch} key={3} />,
            ];
          }
        ),
      ]}
      firstLineBold
    />
  );
};

const CloudPlugsSwitchStatus = (plug: { switch?: boolean }) => {
  return (
    <>
      <span>
        {typeof plug.switch === 'boolean' ? String(plug.switch) : '---'}
      </span>
    </>
  );
};

const CloudPlugsLedModeStatus = ({
  ledMode,
  ledColors,
}: Pick<
  Store['MQTT_CLOUD_DEVICES_STATUS'][number],
  'ledMode' | 'ledColors'
>) => {
  return (
    <Row key={2} gap={0.25}>
      <FormattedMessage
        id="cloud_plugs.led_mode.mode"
        defaultMessage="{mode, select, unknown {---} off {Vypnuto} switch {} power {Podle příkonu} other {Neznámý}}"
        values={{ mode: ledMode }}
      />

      {typeof ledColors === 'object' && (
        <>
          <Circle sx={{ color: `rgb(${ledColors.on.rgb.join()})` }} />
          <Circle sx={{ color: `rgb(${ledColors.off.rgb.join()})` }} />
        </>
      )}
    </Row>
  );
};
